<template>
    <section class="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
        <!-- Header avec logo -->
        <div class="w-full max-w-md flex justify-between items-center mb-8">
            <div class="flex items-center">
                <img src="../assets/logo.jpg" alt="Logo" class="w-10 h-10 rounded-md object-cover mr-3" />
                <h1 class="text-xl font-semibold text-gray-800">Pomme City</h1>
            </div>
            <div class="flex space-x-4">
                <button class="text-gray-500 hover:text-gray-700 transition-colors">
                    <i class="fas fa-circle-info"></i>
                </button>
                <button class="text-gray-500 hover:text-gray-700 transition-colors">
                    <i class="fas fa-circle-question"></i>
                </button>
            </div>
        </div>

        <!-- Carte de réinitialisation -->
        <div class="w-full max-w-md bg-white rounded-xl shadow-sm p-8">
            <template v-if="step === 1">
                <h2 class="text-2xl font-bold text-center text-gray-800 mb-8">Mot de passe oublié</h2>
                <form @submit.prevent="sendCode" class="space-y-6">
                    <div class="space-y-1">
                        <label for="email" class="block text-sm font-medium text-gray-700">Email</label>
                        <input 
                            type="email" 
                            id="email" 
                            v-model="email" 
                            required
                            class="w-full px-4 py-3 outline-none border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
                            placeholder="votre@email.com"
                        />
                    </div>

                    <button 
                        type="submit" 
                        :disabled="isLoading"
                        class="w-full py-3 px-4 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed flex justify-center items-center"
                    >
                        <span v-if="!isLoading">Envoyer le code</span>
                        <span v-else class="flex items-center">
                            <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Envoi en cours...
                        </span>
                    </button>

                    <div v-if="error" class="text-red-500 text-sm text-center p-3 bg-red-50 rounded-lg">
                        {{ error }}
                    </div>
                </form>
            </template>

            <template v-else-if="step === 2">
                <h2 class="text-2xl font-bold text-center text-gray-800 mb-8">Vérification du code</h2>
                <form @submit.prevent="verifyCode" class="space-y-6">
                    <div class="space-y-1">
                        <label for="code" class="block text-sm font-medium text-gray-700">Code de vérification</label>
                        <input 
                            type="text" 
                            id="code" 
                            v-model="code" 
                            required
                            class="w-full px-4 py-3 outline-none border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
                            placeholder="Entrez le code reçu"
                        />
                    </div>

                    <div class="space-y-1">
                        <label for="newPassword" class="block text-sm font-medium text-gray-700">Nouveau mot de passe</label>
                        <div class="relative">
                            <input 
                                :type="showPassword ? 'text' : 'password'"
                                id="newPassword" 
                                v-model="newPassword" 
                                required
                                class="w-full px-4 py-3 outline-none border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all pr-10"
                                placeholder="••••••••"
                            />
                            <button
                                type="button"
                                @click="showPassword = !showPassword"
                                class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                            >
                                <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                            </button>
                        </div>
                    </div>

                    <button 
                        type="submit" 
                        :disabled="isLoading"
                        class="w-full py-3 px-4 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed flex justify-center items-center"
                    >
                        <span v-if="!isLoading">Réinitialiser le mot de passe</span>
                        <span v-else class="flex items-center">
                            <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Réinitialisation...
                        </span>
                    </button>

                    <div v-if="error" class="text-red-500 text-sm text-center p-3 bg-red-50 rounded-lg">
                        {{ error }}
                    </div>
                </form>
            </template>

            <template v-else>
                <div class="text-center space-y-6">
                    <i class="fas fa-check-circle text-5xl text-green-500 mb-4"></i>
                    <h2 class="text-2xl font-bold text-gray-800">Mot de passe réinitialisé !</h2>
                    <p class="text-gray-600">Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.</p>
                    <button 
                        @click="goToLogin"
                        class="w-full py-3 px-4 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
                    >
                        Retour à la connexion
                    </button>
                </div>
            </template>
        </div>

        <!-- Footer -->
        <div class="mt-8 text-center text-sm text-gray-500">
            <p>© 2024 Pomme City. Tous droits réservés. </p>
        </div>
    </section>
</template>

<script setup>
import { ref } from 'vue';
import axios from 'axios';
import { useRouter } from 'vue-router';

const email = ref('');
const code = ref('');
const newPassword = ref('');
const showPassword = ref(false);
const isLoading = ref(false);
const error = ref(null);
const step = ref(1);
const router = useRouter();
  
const sendCode = async () => {
    isLoading.value = true;
    error.value = null;
    try {
        await axios.post('/api/send-reset-code', { email: email.value });
        step.value = 2;
    } catch (err) {
        error.value = err.response?.data?.error || "Erreur lors de l'envoi du code";
    } finally {
        isLoading.value = false;
    }
};
  
const verifyCode = async () => {
    isLoading.value = true;
    error.value = null;
    try {
        await axios.post('/api/verify-code-reset-password', { 
            email: email.value, 
            code: code.value, 
            newPassword: newPassword.value 
        });
        step.value = 3;
    } catch (err) {
        error.value = err.response?.data?.error || "Erreur lors de la réinitialisation";
    } finally {
        isLoading.value = false;
    }
};
  
const goToLogin = () => {
    router.push('/');
};
</script>

<style>
/* Animation pour le spinner */
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.animate-spin {
  animation: spin 1s linear infinite;
}
</style>